





































































































































































import { Component, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import CurrenciesStore from "@/mixins/constants/currenciesStore";

// Enums
import { DataModules } from "@/store/modules/data";

// types
import { IBrand, IBrandStoreSettingsForm } from "@/models/brands";
import { ICurrency } from "@/models/constants";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      brandId(state: IState) {
        return state.brand.id;
      },
      brandScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](this.brandId);
      },
      brand(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.BRANDS,
          scope: this.brandScope
        });
      },
      baseCurrencies(state: IState, getters) {
        return getters[`constants/currencies`]({ baseOnly: true });
      }
    })
  }
})
export default class BrandStoreSettings extends Mixins(
  RequestPromises,
  CurrenciesStore,
  HasValidation
) {
  // Map state

  public brandId!: IBrand["id"];
  public brandScope!: string;
  public brand!: IBrand;
  public baseCurrencies!: ICurrency[];

  // Data

  public form: IBrandStoreSettingsForm = {
    currency_id: "",
    company_phone: "",
    company_email: "",
    company_name: "",
    company_address: "",
    autoapply: 0
  };
  public initialForm: IBrandStoreSettingsForm = _.clone(this.form);

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public savedBrandScope!: string;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.savedBrandScope = this.brandScope;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.BRANDS,
      scope: this.savedBrandScope,
      vm: this
    });
  }

  // Methods

  public async loadData(ignoreStored = false) {
    try {
      await this.getBrandDetails(ignoreStored);
      this.fillForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData(true);
  }

  public getBrandDetails(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.BRANDS}/get`, {
      id: this.brandId,
      scope: this.brandScope,
      ignoreStored,
      vm: this
    });
  }

  public fillForm() {
    this.form = _.pick(this.brand, [
      "currency_id",
      "company_phone",
      "company_email",
      "company_name",
      "company_address",
      "autoapply"
    ]) as IBrandStoreSettingsForm;
    this.initialForm = _.cloneDeep(this.form);
  }

  public revertToInitialState() {
    this.form = _.cloneDeep(this.initialForm);
  }

  public async validateBeforeSubmit() {
    if (!this.canSave) return;

    try {
      this.isProcessing = true;

      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      await this.updateBrand();

      if (_.has(this.newValues, "currency_id"))
        await this.refreshBrandCurrencies();

      await this.getBrandDetails(true);

      this.fillForm();

      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.brand")
        })
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateBrand() {
    return this.$store.dispatch(`data/${DataModules.BRANDS}/update`, {
      id: this.brandId,
      data: this.newValues
    });
  }

  public refreshBrandCurrencies() {
    return this.$store.dispatch("brand/get", { ignoreStored: true });
  }

  // Computed

  public get canSave() {
    return this.$userCan("update_brand");
  }

  public get formIsChanged() {
    return !_.isEqual(this.form, this.initialForm);
  }

  public get newValues() {
    return _.omitBy(this.form, (v, k) => this.initialForm[k] === v);
  }

  // Guarding

  public guardFunctionalities = ["get_brand"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
